// packages/report-components/src/Callout.tsx
//
// Highlighted info / warn / success / danger box. Mantine Alert parity.
//
// Structure: tinted background + matching coloured left border + icon.
// Renders <aside role="note"> so screen-readers announce it separately
// from surrounding prose.

import * as React from "react";

import { usePageDensity } from "./PageDensityContext";
import { PageEyebrow } from "./pageFrameParts";
import {
  IconInfoCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconAlertOctagon,
  type IconProps,
} from "@tabler/icons-react";

export type CalloutKind = "info" | "warn" | "success" | "danger";

export interface CalloutProps {
  /** Severity kind. Defaults to "info". */
  kind?: CalloutKind;
  /** Optional bolded title rendered above the body. */
  title?: string;
  children?: React.ReactNode;
  /** "band" draws the page's closing line instead of an alert: a tinted band
   *  across the cell, the block's own label on it, one sentence inside. A
   *  variant of this component and not a new one on purpose: every compiled
   *  section imports all of `SANDBOX_COMPONENTS`, so a name the sandbox does
   *  not export yet is an ESM link failure of the whole bundle. */
  variant?: "callout" | "band";
}

/** Whether the band has anything to say: its sentence is written into the
 *  child's `text` prop by the narrative pass, and an edition where the pass
 *  failed or never ran leaves it empty. `SectionIntro` already renders null
 *  for empty text, so without this the band would draw as an empty tinted
 *  box with a label on it. */
function hasText(children: React.ReactNode): boolean {
  return React.Children.toArray(children).some((child) => {
    if (typeof child === "string") return child.trim().length > 0;
    if (!React.isValidElement(child)) return false;
    const text = (child.props as { text?: unknown }).text;
    if (typeof text === "string") return text.trim().length > 0;
    // A child that carries no `text` prop is content in its own right.
    return true;
  });
}

type KindStyle = {
  background: string;
  border: string;
  title: string;
  icon: React.ComponentType<IconProps>;
  iconColor: string;
};

const KIND_STYLES: Record<CalloutKind, KindStyle> = {
  info: {
    background: "var(--verity-blue-0, #eef2fc)",
    border: "var(--verity-blue-6, #3455d3)",
    title: "var(--verity-blue-9, #1b2d75)",
    icon: IconInfoCircle,
    iconColor: "var(--verity-blue-6, #3455d3)",
  },
  warn: {
    background: "var(--verity-yellow-0, #fff9db)",
    border: "var(--verity-yellow-7, #f59f00)",
    title: "var(--verity-yellow-8, #f08c00)",
    icon: IconAlertTriangle,
    iconColor: "var(--verity-yellow-7, #f59f00)",
  },
  success: {
    background: "var(--verity-green-0, #ebfbee)",
    border: "var(--verity-green-7, #37b24d)",
    title: "var(--verity-green-8, #2f9e44)",
    icon: IconCircleCheck,
    iconColor: "var(--verity-green-7, #37b24d)",
  },
  danger: {
    background: "var(--verity-red-0, #fff5f5)",
    border: "var(--verity-red-7, #f03e3e)",
    title: "var(--verity-red-8, #e03131)",
    icon: IconAlertOctagon,
    iconColor: "var(--verity-red-7, #f03e3e)",
  },
};

export function Callout({
  kind = "info",
  title,
  children,
  variant = "callout",
}: CalloutProps): React.ReactElement | null {
  const density = usePageDensity();
  if (variant === "band") {
    if (!hasText(children)) return null;
    // Inline styles, like every other part drawn in a page cell: the
    // email-bundle guard forbids a CSS rule about page descendants. Spacings
    // are the density's own numbers, which is what the server priced the cell
    // with, and off a page they fall back to the same values the density
    // carries.
    const padX = density?.band_pad_x_px ?? 16;
    const padY = density?.band_pad_y_px ?? 12;
    return (
      <aside
        role="note"
        className="verity-callout verity-callout--band"
        data-variant="band"
        style={{
          // The band must NOT inherit `.verity-callout`'s geometry, and every
          // one of these three lines is a fault seen on a rendered page. The
          // class is a flex ROW with a 4px left rule and an 8px radius, which
          // is right for an alert with an icon beside its text and wrong for a
          // band: the eyebrow became a flex ITEM beside the sentence (and, being
          // `nowrap` with an ellipsis, clipped to "PROPOSED FO..."), the rule
          // drew in `currentColor` because only the alert kinds set
          // `borderLeftColor` — a heavy black bar nobody chose — and the corners
          // were round where this wants square.
          //
          // Overridden HERE rather than in `components.css` because the
          // email-bundle guard forbids a CSS rule about page descendants, which
          // is the same reason the spacings below are inline. Explicit widths
          // rather than `border: none`: jsdom drops a `none` border from
          // `cssText` entirely, so a test asserting its absence passes whether
          // or not the fix is there.
          display: "block",
          borderLeftWidth: 0,
          borderRadius: 0,
          // Its OWN token, not the generic muted surface. Two reasons. It was
          // `--verity-color-surface-muted` (#f9fafb) against paper at #ffffff,
          // a ~2.5% step that Arjen could barely see on a rendered page. And
          // ten other parts of a report draw on that same token, so tuning the
          // band through it would have moved every card and note with it.
          // `--verity-report-band` is defined in BOTH theme blocks of
          // tokens.css, and the literal is only the no-stylesheet fallback. An
          // earlier cut chained through `--verity-blue-0`, which is a RAW
          // SCALE and by design does not flip, so a dark report page would
          // have drawn a near-white band under light text.
          background: "var(--verity-report-band, #eef2fc)",
          paddingTop: padY,
          paddingRight: padX,
          paddingBottom: padY,
          paddingLeft: padX,
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        {title && density ? (
          <PageEyebrow density={density} className="verity-callout__eyebrow" text={title}>
            {title}
          </PageEyebrow>
        ) : title ? (
          <div className="verity-callout__eyebrow">{title}</div>
        ) : null}
        {children}
      </aside>
    );
  }
  const s = KIND_STYLES[kind];
  const IconEl = s.icon;
  const style: React.CSSProperties = {
    background: s.background,
    borderLeftColor: s.border,
  };
  const titleStyle: React.CSSProperties = {
    color: s.title,
  };

  return (
    <aside
      role="note"
      className="verity-callout"
      data-kind={kind}
      style={style}
    >
      <span className="verity-callout__icon" aria-hidden="true">
        <IconEl size={20} color={s.iconColor} stroke={2} />
      </span>
      <div className="verity-callout__body">
        {title ? (
          <div className="verity-callout__title" style={titleStyle}>
            {title}
          </div>
        ) : null}
        <div className="verity-callout__content">{children}</div>
      </div>
    </aside>
  );
}
